import { useCallback, useEffect, useRef, useState } from 'react'
import { Button } from '@/components/ui/Button'
import { EmptyState, ErrorState, LoadingState, Notice } from '@/components/ui/Feedback'
import type { AdminFetch } from './AdminCustomers'

type Status = 'NEW' | 'ATTENDED'
type Filter = Status | 'ALL'
type Quote = { id: string; fullName: string; email: string; phone: string; businessType: string; businessLabel: string; status: Status; createdAt: string; attendedAt?: string | null }
type QuotePage = { items: Quote[]; page: number; hasMore: boolean }

const filterLabels: Record<Filter, string> = { NEW: 'Nuevas', ATTENDED: 'Atendidas', ALL: 'Todas' }
const when = new Intl.DateTimeFormat('es-MX', { dateStyle: 'medium', timeStyle: 'short' })

/** A phone as WhatsApp wants it: digits with the country code; ten digits are taken as Mexican. */
function whatsappNumber(phone: string) {
  const digits = phone.replace(/\D/g, '')
  return digits.length === 10 ? `52${digits}` : digits
}

/** The quote requests sent from the home page: the newest new ones first, to call back and mark attended. */
export function AdminQuotes({ adminFetch, describeError }: { adminFetch: AdminFetch; describeError: (cause: unknown, fallback: string) => string }) {
  const [filter, setFilter] = useState<Filter>('NEW')
  const [rows, setRows] = useState<Quote[]>([])
  const [page, setPage] = useState(0)
  const [hasMore, setHasMore] = useState(false)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [marking, setMarking] = useState<string | null>(null)
  const [notice, setNotice] = useState('')
  const lastLoad = useRef(0)

  const load = useCallback(async (status: Filter, nextPage = 0) => {
    const ticket = ++lastLoad.current
    setLoading(true); setError('')
    try {
      const result = await adminFetch<QuotePage>(`/admin/quotes?status=${status}&page=${nextPage}`)
      if (ticket !== lastLoad.current) return
      setRows((current) => nextPage === 0 ? result.items : [...current, ...result.items])
      setPage(result.page); setHasMore(result.hasMore)
    } catch (cause) {
      if (ticket === lastLoad.current) setError(describeError(cause, 'No pudimos leer las solicitudes de cotización.'))
    } finally {
      if (ticket === lastLoad.current) setLoading(false)
    }
  }, [adminFetch, describeError])

  useEffect(() => { void load(filter) }, [filter, load])

  async function mark(quote: Quote, status: Status) {
    setMarking(quote.id); setNotice(''); setError('')
    try {
      const updated = await adminFetch<Quote>(`/admin/quotes/${quote.id}`, { method: 'PATCH', body: { status } })
      // A request leaves a filtered list once it no longer matches; under «Todas» it stays with its new status.
      setRows((current) => filter === 'ALL' ? current.map((row) => row.id === updated.id ? updated : row) : current.filter((row) => row.id !== updated.id))
      setNotice(status === 'ATTENDED' ? `${quote.fullName} quedó como atendida.` : `${quote.fullName} volvió a nuevas.`)
    } catch (cause) {
      setError(describeError(cause, 'No pudimos cambiar el estado de la solicitud.'))
    } finally {
      setMarking(null)
    }
  }

  return <section className="admin-customers admin-quotes" aria-labelledby="admin-quotes-heading">
    <div className="admin-section-heading"><div><h2 id="admin-quotes-heading">Solicitudes de cotización</h2><p>Las envían los prospectos desde el inicio del portal. Llámales o escríbeles y márcalas como atendidas.</p></div></div>
    <div className="segmented" role="group" aria-label="Estado de las solicitudes">{(Object.keys(filterLabels) as Filter[]).map((value) => <button key={value} type="button" aria-pressed={filter === value} onClick={() => setFilter(value)}>{filterLabels[value]}</button>)}</div>
    {notice && <Notice tone="success">{notice}</Notice>}
    {error && rows.length > 0 && <Notice>{error}</Notice>}
    {loading && rows.length === 0 ? <LoadingState label="Leyendo las solicitudes…" />
      : error && rows.length === 0 ? <ErrorState title={error} retry={() => void load(filter)} />
      : rows.length === 0 ? <EmptyState title={filter === 'NEW' ? 'No hay solicitudes nuevas' : 'No hay solicitudes'} description="Cuando un prospecto pida una cotización desde el inicio, aparecerá aquí." />
      : <div className="price-table-wrap admin-quotes-table"><table>
        <thead><tr><th><span className="sr-only">Acciones</span></th><th>Recibida</th><th>Nombre</th><th>Giro</th><th>Teléfono</th><th>Correo</th></tr></thead>
        <tbody>{rows.map((quote) => <tr key={quote.id}>
          <td>{quote.status === 'NEW'
            ? <Button emphasis="outline" busy={marking === quote.id} onClick={() => void mark(quote, 'ATTENDED')}>Marcar atendida</Button>
            : <Button emphasis="ghost" intent="neutral" busy={marking === quote.id} onClick={() => void mark(quote, 'NEW')}>Volver a nueva</Button>}</td>
          <td>{when.format(new Date(quote.createdAt))}{quote.attendedAt && <span className="admin-customers-sub">Atendida {when.format(new Date(quote.attendedAt))}</span>}</td>
          <td>{quote.fullName}</td>
          <td>{quote.businessLabel}</td>
          <td><a href={`tel:${quote.phone}`}>{quote.phone}</a><span className="admin-customers-sub"><a href={`https://wa.me/${whatsappNumber(quote.phone)}`} target="_blank" rel="noreferrer">Abrir WhatsApp</a></span></td>
          <td><a href={`mailto:${quote.email}`}>{quote.email}</a></td>
        </tr>)}</tbody>
      </table></div>}
    {hasMore && <div className="admin-customers-more"><Button emphasis="outline" intent="neutral" busy={loading} onClick={() => void load(filter, page + 1)}>Ver más</Button></div>}
  </section>
}
